import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_BASE_URL from "../../apis/api";
import {API_INTG_URL} from "../../apis/api"; 

const api = axios.create({
  baseURL: API_INTG_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ✅ Axios interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      console.warn("⚠️ Access token expired. Attempting to refresh...");
      try {
        const refreshToken = localStorage.getItem("refresh_token");
        if (!refreshToken) throw new Error("No refresh token available");
        const response = await api.post("/api/auth/token/refresh/", {
          refresh: refreshToken,
        });
        const { access } = response.data;
        localStorage.setItem("access_token", access);
        setAuthToken(access);
        // Retry the failed request with the new token
        error.config.headers["Authorization"] = `Bearer ${access}`;
        return api.request(error.config);
      } catch (refreshError) {
        console.error("❌ Token refresh failed. Logging out...");
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        window.location.href = "/"; // Redirect to login
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

// ✅ Function to set the Authorization token
export const setAuthToken = (token) => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  };

// ✅ Thunk to fetch shipments (cases) by email
export const fetchShipmentsByEmail = createAsyncThunk(
  "shipments/fetchByEmail",
  async (email) => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await api.get(`/api/supplychain/cases/?email=${email}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("✅ Shipments (Cases) fetched:", response.data);

      return response.data.map((shipment) => ({
        ...shipment,
        current_status: shipment.current_status || "open",
        is_pinned: shipment.is_pinned || false,
      }));
    } catch (error) {
      console.error("❌ Failed to fetch shipments:", error.response?.data || error.message);
    }
  }
);





// Thunk to fetch shipments by threadId
export const fetchShipmentsByThreadId = createAsyncThunk(
  "shipments/fetchByThreadId",
  async (threadId) => {
    const response = await axios.get(`${API_BASE_URL}/shipments?threadId=${threadId}`);
    return response.data; // Assuming the response is an array of shipments for the given threadId
  }
);

// Thunk to update shipment status
export const updateShipmentStatus = createAsyncThunk(
  "shipments/updateStatus",
  async ({ shipmentId, status }) => {
    const response = await axios.patch(`${API_BASE_URL}/shipments/${shipmentId}`, {
      status,
    });
    return { shipmentId, status: response.data.status };
  }
);

// ✅ Thunk to close a shipment
export const closeShipment = createAsyncThunk(
  "shipments/closeShipment",
  async (caseNumber, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("No authentication token found. Please log in.");

      const response = await api.patch(`/api/supplychain/cases/${caseNumber}/close/`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log(`✅ Shipment ${caseNumber} closed successfully:`, response.data);
      return { caseNumber, status: "closed" };
    } catch (error) {
      console.error(`❌ Failed to close shipment ${caseNumber}:`, error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ✅ Thunk to open a shipment
export const openShipment = createAsyncThunk(
  "shipments/openShipment",
  async (caseNumber, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) throw new Error("No authentication token found. Please log in.");

      const response = await api.patch(`/api/supplychain/cases/${caseNumber}/open/`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log(`✅ Shipment ${caseNumber} reopened successfully:`, response.data);
      return { caseNumber, status: "open" };
    } catch (error) {
      console.error(`❌ Failed to open shipment ${caseNumber}:`, error.response?.data || error.message);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);


// Thunk to update MandatoryData
export const updateMandatoryDataThunk = createAsyncThunk(
  "shipments/updateMandatoryData",
  async ({ shipmentId, mandatoryData }, { getState }) => {
    // Persist changes to the backend
    await axios.patch(`${API_BASE_URL}/shipments/${shipmentId}`, {
      MandatoryData: mandatoryData,
    });

    // Return updated MandatoryData and shipmentId
    return { shipmentId, mandatoryData };
  }
);

// Thunk to update shipment registration data
export const updateShipmentDataThunk = createAsyncThunk(
  "shipments/updateShipmentData",
  async ({ shipmentId, mandatoryData }, { getState }) => {
    // Persist changes to the backend
    await axios.patch(`${API_BASE_URL}/shipments/${shipmentId}`, {
      ShipmentRegister: mandatoryData,
    });

    // Return updated MandatoryData and shipmentId
    return { shipmentId, mandatoryData };
  }
);



// Thunk to fetch all shipments
export const fetchShipments = createAsyncThunk(
  "shipments/fetchAll",
  async () => {
    console.log({API_BASE_URL})
    const response = await axios.get(`${API_BASE_URL}/shipments`);
    return response.data; 
  }
);

// Thunk to update shipment issues
export const updateShipmentIssue = createAsyncThunk(
  "shipments/updateIssue",
  async ({ shipmentId }, { rejectWithValue }) => {
    try {
      // Make a PATCH request to set "issue" to false
      const response = await axios.patch(`${API_BASE_URL}/shipments/${shipmentId}`, {
        issue: false, // Synchronize "issue" to false
      });
      return response.data; // Return the response data
    } catch (error) {
      console.error("Failed to update shipment issue:", error);
      return rejectWithValue(error.response?.data || "Failed to update shipment issue");
    }
  }
);

// Thunk to toggle the pin state
export const updatePinnedShipments = createAsyncThunk(
  "shipments/updatePinned",
  async ({ caseNumber, isTagged }, { dispatch }) => {
    const token = localStorage.getItem("access_token");
    if (!token) throw new Error("No authentication token found. Please log in.");
    try {
      
      // Send the update request to the backend
      const response = await api.patch(
        `/api/supplychain/cases/${caseNumber}/`, {
        is_pinned: isTagged,
      },{
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Server response for pin update:", response.data);

      // Update local Redux state
      dispatch(toggleShipmentPin({ caseNumber, isTagged }));

      return response.data;
    } catch (error) {
      console.error("Failed to update pin state:", error);
      throw error;
    }
  }
);


const shipmentsSlice = createSlice({
  name: "shipments",
  initialState: {
    shipments: [], // List of shipments
    shipmentsByThreadId: {}, // Object to store shipments grouped by threadId
    status: "idle", // loading, succeeded, failed
    pinnedShipments: [], // List of pinned shipment IDs
  },
  reducers: {
    updateShipmentState: (state, action) => {
      const { shipmentId, status } = action.payload;
      const shipment = state.shipments.find((s) => s.shipmentId === shipmentId);
      if (shipment) shipment.status = status;
    },
    decrementShipmentIssue: (state, action) => {
      const { shipmentId } = action.payload;
      const shipment = state.shipments.find((s) => s.shipmentId === shipmentId);
      if (shipment?.shipmentDetails?.issue > 0) {
        shipment.shipmentDetails.issue -= 1;
      }
    },
    updateMandatoryData: (state, action) => {
      const { shipmentId, mandatoryData } = action.payload;
      const shipment = state.shipments.find(
        (s) => String(s.shipmentId) === String(shipmentId)
      );
      if (shipment) {
        shipment.MandatoryData = mandatoryData; // Update locally
      }
    },
    updateShipmentData: (state, action) => {
      const { shipmentId, mandatoryData } = action.payload;
      const shipment = state.shipments.find(
        (s) => String(s.shipmentId) === String(shipmentId)
      );
      if (shipment) {
        shipment.ShipmentRegister = mandatoryData; // Update locally
      }
    },
    toggleShipmentPin: (state, action) => {
      const { caseNumber, isTagged } = action.payload;
      const shipment = state.shipments.find((s) => s.case_number === caseNumber);
      if (shipment) {
        shipment.is_pinned = isTagged; // ✅ Properly update existing shipment in Redux state
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ✅ Handle closing shipment
      .addCase(closeShipment.pending, (state) => {
        state.status = "loading";
      })
      .addCase(closeShipment.fulfilled, (state, action) => {
        const { caseNumber, status } = action.payload;
        const shipment = state.shipments.find((s) => s.case_number === caseNumber);
        if (shipment) shipment.current_status = status;
        state.status = "succeeded";
      })
      .addCase(closeShipment.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })

      // ✅ Handle opening shipment
      .addCase(openShipment.pending, (state) => {
        state.status = "loading";
      })
      .addCase(openShipment.fulfilled, (state, action) => {
        const { caseNumber, status } = action.payload;
        const shipment = state.shipments.find((s) => s.case_number === caseNumber);
        if (shipment) shipment.current_status = status;
        state.status = "succeeded";
      })
      .addCase(openShipment.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(fetchShipmentsByEmail.pending, (state) => {
        state.status = "loading"
        state.loading = true;
      })
      .addCase(fetchShipmentsByEmail.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.loading = false;
        state.shipments = action.payload; // This should contain the modified shipment data with `is_pinned`
      })
      .addCase(fetchShipmentsByEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch shipments by threadId
      .addCase(fetchShipmentsByThreadId.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchShipmentsByThreadId.fulfilled, (state, action) => {
        const threadId = action.meta.arg; // Extract the threadId from the thunk arguments
        state.shipmentsByThreadId[threadId] = action.payload;
        state.status = "succeeded";
      })
      .addCase(fetchShipmentsByThreadId.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Handle updating MandatoryData
      .addCase(updateMandatoryDataThunk.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateMandatoryDataThunk.fulfilled, (state, action) => {
        const { shipmentId, mandatoryData } = action.payload;
        const shipment = state.shipments.find(
          (s) => String(s.shipmentId) === String(shipmentId)
        );
        if (shipment) {
          shipment.MandatoryData = mandatoryData; // Update Redux store
        }
        state.status = "succeeded";
      })
      .addCase(updateMandatoryDataThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Handle updating ShipmentData
      .addCase(updateShipmentDataThunk.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateShipmentDataThunk.fulfilled, (state, action) => {
        const { shipmentId, mandatoryData } = action.payload;
        const shipment = state.shipments.find(
          (s) => String(s.shipmentId) === String(shipmentId)
        );
        if (shipment) {
          shipment.ShipmentRegister = mandatoryData; // Update Redux store
        }
        state.status = "succeeded";
      })
      .addCase(updateShipmentDataThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Handle fetching all shipments
      .addCase(fetchShipments.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchShipments.fulfilled, (state, action) => {
        state.shipments = action.payload;
        state.status = "succeeded";
      })
      .addCase(fetchShipments.rejected, (state) => {
        state.status = "failed";
      })

      // Handle updating shipment issues
      .addCase(updateShipmentIssue.fulfilled, (state) => {
        state.status = "succeeded";
      })

      // Handle updating shipment status
      .addCase(updateShipmentStatus.fulfilled, (state, action) => {
        const { shipmentId, status } = action.payload;
        const shipment = state.shipments.find((s) => s.shipmentId === shipmentId);
        if (shipment) shipment.status = status;
        state.status = "succeeded";
      })

      // Handle updating pinned shipments
      .addCase(updatePinnedShipments.fulfilled, (state) => {
        state.status = "succeeded";
      });
  },
});

export const {
  updateMandatoryData,
  updateShipmentData,
  decrementShipmentIssue,
  toggleShipmentPin,
} = shipmentsSlice.actions;

export default shipmentsSlice.reducer;